/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import useGetBillingCloudResources from '@modules/billing-lib/queries/useGetBillingCloudResources'
import useOrganizationId from '@modules/billing-lib/hooks/useOrganizationId'
import useGetUsageChartsData from '@modules/billing-lib/queries/useGetUsageChartsData'

import {
  getAccessors,
  getCloudResourceMap,
  getCustomYDomain,
  getExistingScopeValues,
  getSeriesName,
} from './utils'

import type { BudgetConfigurationValues } from '../../../types'

type Props = {
  amount: BudgetConfigurationValues['amount']
  dateFrom: string
  dateTo: string
  scopeType: BudgetConfigurationValues['scopeType']
  scopeValues: BudgetConfigurationValues['scopeValues']
}

const useCostTrendsChart = ({ amount, dateFrom, dateTo, scopeType, scopeValues }: Props) => {
  const organizationId = useOrganizationId()

  const { data: billingCloudResources, isLoading: isLoadingBillingCloudResources } =
    useGetBillingCloudResources(organizationId)

  const {
    data: { data: usageChartData } = {},
    isError,
    isLoading: isLoadingChartData,
  } = useGetUsageChartsData({
    pathParameters: {
      organization_id: organizationId,
    },
    queryParameters: {
      from: dateFrom,
      to: dateTo,
      instance_type: 'all',
      bucketing_strategy: 'monthly',
    },
  })

  const cloudResourceMap = getCloudResourceMap(billingCloudResources)
  const existingScopeValues = getExistingScopeValues(scopeValues, cloudResourceMap)

  const yAccessors = getAccessors(scopeType, existingScopeValues, cloudResourceMap)

  // Because the x-axis is ordinal, we need to define the domain.
  const xDomain = usageChartData?.map(({ timestamp }) => timestamp)

  const amountValue = Number(amount || 0)
  const customYDomain = getCustomYDomain({
    amountValue,
    usageChartData,
    yAccessors,
  })

  const seriesName = getSeriesName({
    scopeType,
    yAccessors,
  })

  return {
    amountValue,
    usageChartData,
    customYDomain,
    isLoading: isLoadingBillingCloudResources || isLoadingChartData,
    isError,
    seriesName,
    xDomain,
    yAccessors,
  }
}

export default useCostTrendsChart
